import React, { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import MainLayout from '@/components/MainLayout'
import { useLocalizedNavigate } from '@/hooks/useLocalizedNavigate'
import RefineWrapper from '@/components/refine/RefineWrapper'
import ContextStep from './ContextStep'
import RoleStep from './RoleStep'
import ActionStep from './ActionStep'
import FormatStep from './FormatStep'
import ToneStep from './ToneStep'
import ExampleStep from './ExampleStep'
import { PromptProvider, usePrompt } from '@/contexts/PromptContext'
import { ApiService } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { storageService } from '@/services/storageService'
import { useAutoResizeTextarea } from '@/hooks/useAutoResizeTextarea'
import { useAuth } from '@/contexts/AuthContext'

// Extend window interface for timeout and pending saves
declare global {
  interface Window {
    contextUpdateTimeout?: NodeJS.Timeout
    contextPendingSave?: () => Promise<void>
    actionUpdateTimeout?: NodeJS.Timeout
    actionPendingSave?: () => Promise<void>
    exampleUpdateTimeout?: NodeJS.Timeout
    examplePendingSave?: () => Promise<void>
  }
}

interface RefineStepProps {}

const RefineContent: React.FC = () => {
  const { t } = useTranslation(['steps', 'common'])
  const [openAccordion, setOpenAccordion] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const [finalPrompt, setFinalPrompt] = useState('')
  const [isGenerating, setIsGenerating] = useState(false)
  const [hasGenerated, setHasGenerated] = useState(false)
  const { promptData, updatePrompt, refreshPrompt, loading } = usePrompt()
  const { user } = useAuth()
  const placeholder = t('steps:refine.placeholder')
  const textareaRef = useAutoResizeTextarea(finalPrompt, placeholder)
  
  // Force resize when textarea mounts with existing content
  useEffect(() => {
    if (textareaRef.current && finalPrompt && !isGenerating) {
      // Small delay to ensure textarea is fully rendered
      const timer = setTimeout(() => {
        const textarea = textareaRef.current
        if (textarea) {
          textarea.style.height = 'auto'
          const minHeight = 48
          const newHeight = Math.max(textarea.scrollHeight + 2, minHeight)
          textarea.style.height = `${newHeight}px`
        }
      }, 0)
      return () => clearTimeout(timer)
    }
  }, [finalPrompt, isGenerating, textareaRef])
  
  // Debounced save for manual edits
  useEffect(() => {
    if (!promptData?.id || !hasGenerated) return
    
    const timeoutId = setTimeout(async () => {
      if (finalPrompt && finalPrompt !== promptData.final_prompt) {
        try {
          await updatePrompt({ final_prompt: finalPrompt })
        } catch (error) {
          console.error('Error saving final prompt:', error)
        }
      }
    }, 1000) // 1 second debounce
    
    return () => clearTimeout(timeoutId)
  }, [finalPrompt, promptData?.id, promptData?.final_prompt, hasGenerated, updatePrompt])

  // Reset hasGenerated when promptData changes (when navigating between prompts)
  useEffect(() => {
    setHasGenerated(false)
  }, [promptData?.id])

  const fetchLatestPromptData = async () => {
    if (!promptData?.id) return null

    try {
      const data = await storageService.loadPrompt(promptData.id, user)
      return data
    } catch (error) {
      console.error('Failed to fetch latest prompt data:', error)
      return null
    }
  }

  const extractPromptInputs = (data: typeof promptData) => {
    if (!data) {
      return null
    }

    const contextOutput = data.context?.context_output || ''
    const selectedRole = data.role?.selected_role || ''
    const action = data.action || ''

    const formatKey = Object.keys(data.format || {}).find(
      (key) => data.format[key] === true
    )
    const format = formatKey ? formatKey.replace(/_/g, ' ') : ''

    const toneParts: string[] = []
    if (data.tone_scores) {
      const { casual_professional, friendly_serious, inviting_neutral, calm_energetic } = data.tone_scores
      
      // Format sliders with clear interpretation
      const formatSlider = (value: number, leftLabel: string, rightLabel: string) => {
        if (value <= 3) {
          return `${leftLabel} (strong preference, ${value}/10)`
        } else if (value >= 7) {
          return `${rightLabel} (strong preference, ${value}/10)`
        } else {
          return `Balanced between ${leftLabel} and ${rightLabel} (${value}/10)`
        }
      }
      
      if (casual_professional !== undefined) {
        toneParts.push(formatSlider(casual_professional, 'Casual', 'Professional'))
      }
      if (friendly_serious !== undefined) {
        toneParts.push(formatSlider(friendly_serious, 'Friendly', 'Serious'))
      }
      if (inviting_neutral !== undefined) {
        toneParts.push(formatSlider(inviting_neutral, 'Inviting', 'Neutral'))
      }
      if (calm_energetic !== undefined) {
        toneParts.push(formatSlider(calm_energetic, 'Calm', 'Energetic'))
      }
      
      // Extract additional tones from tone_scores
      const additionalToneKeys = [
        'adventurous',
        'commanding',
        'conversational',
        'educational',
        'emotional',
        'exciting',
        'fact_based',
        'humorous',
        'industrious',
        'informative',
        'innovative',
        'storytelling',
        'testimonial'
      ]
      const selectedAdditionalTones = additionalToneKeys.filter(key => data.tone_scores[key] === true)
      if (selectedAdditionalTones.length > 0) {
        toneParts.push(`Also incorporate these tone qualities: ${selectedAdditionalTones.map(t => t.replace(/_/g, ' ')).join(', ')}`)
      }
    }
    const tone = toneParts.join('. ')

    const example = data.example || ''
    const originalPrompt = data.original_prompt || ''

    return {
      originalPrompt,
      contextOutput,
      selectedRole,
      action,
      format,
      tone,
      example,
    }
  }

  // Load existing final prompt or generate if conditions are met
  useEffect(() => {
    const handleFinalPrompt = async () => {
      if (!promptData) return

      // If final prompt already exists, just load it
      if (promptData.final_prompt) {
        setFinalPrompt(promptData.final_prompt)
        setHasGenerated(true)
        return
      }

      // Check if Example step is completed (required for generation)
      // Example can be empty, but the step should be "completed" (meaning user has gone through it)
      const hasExampleStepCompleted = promptData.example !== undefined

      // Only generate if Example step is completed and we haven't generated yet
      if (hasExampleStepCompleted && !hasGenerated) {
        setIsGenerating(true)
        try {
          const latestData = await fetchLatestPromptData()
          const dataToUse = extractPromptInputs(latestData || promptData)

          if (!dataToUse) {
            return
          }

          const { originalPrompt, contextOutput, selectedRole, action, format, tone, example } = dataToUse

          const response = await ApiService.generateFinalPrompt(
            originalPrompt,
            contextOutput,
            selectedRole,
            action,
            format,
            tone,
            example
          )

          if (response.success && response.finalPrompt) {
            setFinalPrompt(response.finalPrompt)
            setHasGenerated(true)
            // Update the database with the final prompt
            await updatePrompt({ final_prompt: response.finalPrompt })
          }
        } catch (error) {
          console.error('Failed to generate final prompt:', error)
        } finally {
          setIsGenerating(false)
        }
      }
    }

    handleFinalPrompt()
  }, [promptData, hasGenerated, updatePrompt])

  const handleCopyPrompt = async () => {
    const textarea = document.querySelector('textarea') as HTMLTextAreaElement
    if (textarea) {
      try {
        await navigator.clipboard.writeText(textarea.value)
        setCopied(true)
        // Reset the copied state after 2 seconds
        setTimeout(() => setCopied(false), 2000)
      } catch (err) {
        console.error('Failed to copy text: ', err)
      }
    }
  }

  const handleCopyToChatGPT = () => {
    const textarea = document.querySelector('textarea') as HTMLTextAreaElement
    if (textarea && textarea.value.trim()) {
      const prompt = encodeURIComponent(textarea.value)
      const chatgptUrl = `https://chatgpt.com/?q=${prompt}`
      window.open(chatgptUrl, '_blank')
    }
  }

  const regenerateFinalPrompt = async () => {
    if (!promptData) return

    setIsGenerating(true)
    try {
      const latestData = await fetchLatestPromptData()
      const dataToUse = extractPromptInputs(latestData || promptData)

      if (!dataToUse) {
        throw new Error('No prompt data available for regeneration')
      }

      const { originalPrompt, contextOutput, selectedRole, action, format, tone, example } = dataToUse

      const response = await ApiService.generateFinalPrompt(
        originalPrompt,
        contextOutput,
        selectedRole,
        action,
        format,
        tone,
        example
      )

      if (response.success && response.finalPrompt) {
        setFinalPrompt(response.finalPrompt)
        setHasGenerated(true)
        // Update the database with the final prompt
        await updatePrompt({ final_prompt: response.finalPrompt })
      }
    } catch (error) {
      console.error('Failed to regenerate final prompt:', error)
    } finally {
      setIsGenerating(false)
    }
  }

  const handleAccordionToggle = async (stepId: string) => {
    const isOpening = openAccordion !== stepId
    
    if (isOpening) {
      // Refresh data when opening a step to ensure we have the latest data
      console.log(`Opening ${stepId} step - refreshing data...`)
      await refreshPrompt()
    }
    
    setOpenAccordion(isOpening ? stepId : null)
  }

  const handleSaveStep = async (stepName: string) => {
    console.log(`Saving changes for ${stepName} step`)
    
    // Execute any pending saves immediately instead of waiting for debounce
    const pendingSaves: Promise<void>[] = []
    
    if (window.contextPendingSave) {
      // Clear the timeout since we're executing immediately
      if (window.contextUpdateTimeout) {
        clearTimeout(window.contextUpdateTimeout)
      }
      pendingSaves.push(window.contextPendingSave())
    }
    
    if (window.actionPendingSave) {
      // Clear the timeout since we're executing immediately
      if (window.actionUpdateTimeout) {
        clearTimeout(window.actionUpdateTimeout)
      }
      pendingSaves.push(window.actionPendingSave())
    }
    
    if (window.examplePendingSave) {
      // Clear the timeout since we're executing immediately
      if (window.exampleUpdateTimeout) {
        clearTimeout(window.exampleUpdateTimeout)
      }
      pendingSaves.push(window.examplePendingSave())
    }
    
    // Wait for all pending saves to complete
    if (pendingSaves.length > 0) {
      await Promise.all(pendingSaves)
    }
    
    // Refresh to get the latest data from database
    await refreshPrompt()
    
    // Close the accordion tab
    setOpenAccordion(null)
    
    // Scroll to the top of the page
    window.scrollTo({ top: 0, behavior: 'smooth' })
    
    // Regenerate the final prompt with the latest data
    await regenerateFinalPrompt()
  }

  const renderAccordionContent = (stepName: string) => {
    switch (stepName) {
      case 'context':
        return (
          <RefineWrapper onSave={() => handleSaveStep('context')}>
            <ContextStep />
          </RefineWrapper>
        )
      case 'role':
        return (
          <RefineWrapper onSave={() => handleSaveStep('role')}>
            <RoleStep />
          </RefineWrapper>
        )
      case 'action':
        return (
          <RefineWrapper onSave={() => handleSaveStep('action')}>
            <ActionStep />
          </RefineWrapper>
        )
      case 'format':
        return (
          <RefineWrapper onSave={() => handleSaveStep('format')}>
            <FormatStep />
          </RefineWrapper>
        )
      case 'tone':
        return (
          <RefineWrapper onSave={() => handleSaveStep('tone')}>
            <ToneStep />
          </RefineWrapper>
        )
      case 'example':
        return (
          <RefineWrapper onSave={() => handleSaveStep('example')}>
            <ExampleStep />
          </RefineWrapper>
        )
      default:
        return null
    }
  }

  return (
    <>
      {/* Top Section: Generated Prompt */}
      <div className="text-center mb-8 sm:mb-12 px-4">
          <h2 className="text-2xl sm:text-3xl font-semibold text-gray-900 mb-4">
            {t('steps:refine.title')}
          </h2>

          <p className="text-xs sm:text-base text-gray-600 mb-6 sm:mb-8 max-w-2xl mx-auto">
            {t('steps:refine.description')}
          </p>

          {/* Action buttons */}
          {finalPrompt && !isGenerating && (
            <div className="mb-4 flex flex-col sm:flex-row gap-2 sm:gap-3 justify-center items-stretch sm:items-center px-4">
              <Button
                onClick={regenerateFinalPrompt}
                variant="outline"
                size="sm"
                className="w-full sm:w-auto"
              >
                {t('common:actions.regenerate')}
              </Button>
              <Button
                onClick={handleCopyPrompt}
                variant="outline"
                size="sm"
                disabled={!finalPrompt.trim()}
                className="w-full sm:w-auto"
              >
                {t('common:actions.copy')}
              </Button>
              <Button
                onClick={handleCopyToChatGPT}
                variant="default"
                size="sm"
                disabled={!finalPrompt.trim()}
                className="w-full sm:w-auto"
              >
                {t('common:actions.copyToChatGPT')}
              </Button>
            </div>
          )}

          <div className="relative max-w-3xl mx-auto">
            {isGenerating ? (
              <div className="w-full p-8 text-center text-gray-500 bg-gray-50 border border-gray-200 rounded-lg">
                <div className="flex items-center justify-center space-x-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-500"></div>
                  <span>{t('steps:refine.generating')}</span>
                </div>
              </div>
            ) : (
              <textarea
                ref={textareaRef}
                value={finalPrompt}
                onChange={(e) => setFinalPrompt(e.target.value)}
                className="w-full min-h-12 p-4 text-gray-700 bg-gray-50 border border-gray-200 rounded-lg resize-none overflow-y-auto focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent"
                placeholder={t('steps:refine.placeholder')}
                rows={1}
              />
            )}
          </div>

          {/* Success Message */}
          {copied && (
            <div className="mt-4 text-center">
              <div className="text-sm text-green-600 font-medium">
                {t('common:status.copied')}
              </div>
            </div>
          )}
        </div>

        {/* Bottom Section: Refine Accordion */}
        <div className="text-center mb-6 sm:mb-8 px-4">
          <h3 className="text-2xl sm:text-3xl font-semibold text-gray-900">
            {t('steps:refine.refineTitle')}
          </h3>
        </div>

        {/* Accordion */}
        <div className="space-y-0 border border-gray-200 rounded-lg overflow-hidden">
          {[
            { id: 'context', titleKey: 'progress.context', component: ContextStep },
            { id: 'role', titleKey: 'progress.role', component: RoleStep },
            { id: 'action', titleKey: 'progress.action', component: ActionStep },
            { id: 'format', titleKey: 'progress.format', component: FormatStep },
            { id: 'tone', titleKey: 'progress.tone', component: ToneStep },
            { id: 'example', titleKey: 'progress.example', component: ExampleStep }
          ].map((item) => (
            <div key={item.id} className="border-b border-gray-200 last:border-b-0">
              <button
                onClick={() => handleAccordionToggle(item.id)}
                className="w-full px-6 py-4 text-left hover:bg-gray-50 transition-colors flex items-center justify-between"
              >
                <div className="flex items-center">
                  <span className="text-lg font-semibold text-gray-900 mr-3">
                    {t(`common:${item.titleKey}`).charAt(0).toUpperCase()}
                  </span>
                  <span className="text-lg font-semibold text-gray-900">
                    {t(`common:${item.titleKey}`)}
                  </span>
                </div>
                <svg
                  className={`w-5 h-5 text-gray-500 transition-transform ${
                    openAccordion === item.id ? 'rotate-180' : ''
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
              
              {openAccordion === item.id && (
                <div className="px-6 pb-6 border-t border-gray-200">
                  {loading ? (
                    <div className="py-8 text-center">
                      <div className="flex items-center justify-center space-x-2">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-500"></div>
                        <span className="text-gray-500">{t('steps:refine.loadingStep')}</span>
                      </div>
                    </div>
                  ) : (
                    renderAccordionContent(item.id)
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
    </>
  )
  }

const RefineStep: React.FC<RefineStepProps> = () => {
  const { t } = useTranslation(['steps', 'common'])
  const navigate = useLocalizedNavigate()
  const location = useLocation()

  // Get promptId from navigation state
  const promptId = location.state?.promptId

  // If no promptId, redirect to home
  if (!promptId) {
    return (
      <MainLayout>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-4">
              {t('steps:refine.noPromptTitle')}
            </h2>
            <p className="text-xs sm:text-base text-gray-600 mb-6 sm:mb-8">
              {t('steps:refine.noPromptDescription')}
            </p>
            <button
              onClick={() => navigate('/')}
              className="px-6 py-3 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors"
            >
              {t('common:actions.goToHome')}
            </button>
          </div>
        </div>
      </MainLayout>
    )
  }

  return (
    <PromptProvider key={promptId} promptId={promptId}>
      <MainLayout>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <RefineContent />
        </div>
      </MainLayout>
    </PromptProvider>
  )
}

export default RefineStep
